import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import {
	Select,
	SelectContent,
	SelectItemLink,
	SelectTrigger,
	SelectValue
} from '@/components/Select';
import { useIsCrawler } from '@/hooks';
import LangIcon from '@/icons/icon-language.svg';
import { setLanguage } from '@/store/profile/index';
import { capitalize, cn } from '@/utils/index';
import { addLanguageToRoute } from '@/utils/locale';

import { useLanguageOptions } from './hooks';

function Flag({ label, footer }) {
	return (
		<div className={cn('flex items-center gap-2', footer ? 'px-1' : 'px-0')}>
			{footer && <LangIcon className="text-inherit" />}
			<label className="inline-block grow whitespace-nowrap text-inherit">
				{label}
			</label>
		</div>
	);
}

function LanguageSelector({ footer }) {
	const { t } = useTranslation();
	const langOptions = useLanguageOptions();
	const dispatch = useDispatch();
	const lang = useSelector(state => state.profile.language);
	const { pathname } = useLocation();
	const isCrawler = useIsCrawler();
	const navigate = useNavigate();

	function onChange(value) {
		dispatch(setLanguage(value));
		navigate(addLanguageToRoute(pathname, value));
	}

	return (
		<>
			<Select
				value={lang}
				onValueChange={onChange}
				required
				className={cn(footer ? 'bg-black' : 'bg-white')}
				defaultOpen={isCrawler}
			>
				<SelectTrigger
					className={cn(
						'group p-0',
						footer
							? 'border-none bg-black text-white hover:text-red'
							: 'border-gray-200 bg-white text-grey-500 hover:text-grey-500'
					)}
					aria-label={t('labels.language')}
				>
					<SelectValue>
						<Flag
							label={langOptions.filter(l => l.value === lang)[0].label}
							footer={footer}
						/>
					</SelectValue>
				</SelectTrigger>
				<SelectContent
					className="bg-white text-grey-500"
					noPortal={isCrawler}
				>
					{langOptions.map(({ value, label }, index) => (
						<SelectItemLink
							className={cn(
								footer
									? 'text-grey-500 hover:bg-grey-100 data-[state=checked]:bg-red-background data-[state=checked]:text-red'
									: 'text-grey-500 hover:bg-grey-100 data-[state=checked]:bg-grey-100 data-[state=checked]:text-black'
							)}
							key={index}
							value={value}
							to={addLanguageToRoute(pathname, value)}
							linkProps={{ hrefLang: value }}
						>
							{capitalize(label)}
						</SelectItemLink>
					))}
				</SelectContent>
			</Select>
			<div style={isCrawler ? {} : { display: 'none' }}>
				{langOptions.map(({ value, label }, index) => (
					<Link
						key={index}
						to={addLanguageToRoute(pathname, value)}
						hrefLang={value}
					>
						{capitalize(label)}
					</Link>
				))}
			</div>
		</>
	);
}

export default LanguageSelector;
